import { useMemo, useState } from "react";
import { ExternalLink, FileText } from "lucide-react";
import { getSiteContent, hospitalityCatalogConfig } from "@/data/siteContent";
import { useLanguage } from "@/context/LanguageContext";

export default function HospitalityCatalogPage() {
  const { language } = useLanguage();
  const content = getSiteContent(language);
  const [visibleCount, setVisibleCount] = useState(8);

  const allPageImages = useMemo(
    () => {
      const hiddenPages = new Set(hospitalityCatalogConfig.hiddenPages || []);

      return Array.from({ length: hospitalityCatalogConfig.pageCount }, (_, idx) => idx + 1)
        .filter((pageNum) => !hiddenPages.has(pageNum))
        .map((pageNum) => {
          const pageNumber = String(pageNum).padStart(3, "0");
          return `${hospitalityCatalogConfig.pagesFolder}/page-${pageNumber}.jpg`;
        });
    },
    [],
  );

  const visiblePages = allPageImages.slice(0, visibleCount);
  const hasMore = visibleCount < allPageImages.length;

  return (
    <section className="bg-white py-16 md:py-24" data-testid="hospitality-catalog-page">
      <div className="container mx-auto max-w-7xl px-4 md:px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center" data-testid="hospitality-catalog-header">
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl" data-testid="hospitality-catalog-title">
            {content.hospitalityCatalog.title}
          </h1>
          <p className="mt-4 text-base text-slate-600 md:text-lg" data-testid="hospitality-catalog-subtitle">
            {content.hospitalityCatalog.subtitle}
          </p>
          <a
            href={hospitalityCatalogConfig.pdfUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-5 inline-flex h-11 items-center gap-2 rounded-md bg-primary px-5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-primary/90"
            data-testid="hospitality-catalog-open-pdf-button"
          >
            <ExternalLink className="h-4 w-4" /> {content.hospitalityCatalog.openPdf}
          </a>
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="hospitality-catalog-sections-block">
          <div className="mb-3 flex items-center gap-2">
            <FileText className="h-5 w-5 text-secondary" />
            <h2 className="text-lg font-bold text-slate-900" data-testid="hospitality-catalog-sections-title">
              {content.hospitalityCatalog.sectionsTitle}
            </h2>
          </div>
          <ul className="grid grid-cols-1 gap-2 md:grid-cols-2" data-testid="hospitality-catalog-sections-list">
            {content.hospitalityCatalog.sections.map((section, index) => (
              <li
                key={`hospitality-catalog-section-${index}`}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
                data-testid={`hospitality-catalog-section-item-${index}`}
              >
                {section}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-6" data-testid="hospitality-catalog-images-preview-block">
          <h3 className="mb-3 text-lg font-bold text-slate-900" data-testid="hospitality-catalog-images-preview-title">
            {content.hospitalityCatalog.pagesTitle}
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="hospitality-catalog-images-preview-grid">
            {visiblePages.map((imgUrl, index) => (
              <div key={`hospitality-preview-${index}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                <img
                  src={imgUrl}
                  alt={`hospitality catalog page ${index + 1}`}
                  className="w-full object-contain"
                  loading="lazy"
                  data-testid={`hospitality-catalog-preview-image-${index + 1}`}
                />
              </div>
            ))}
          </div>

          {hasMore ? (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => setVisibleCount((prev) => Math.min(prev + 8, allPageImages.length))}
                className="inline-flex h-10 items-center rounded-md bg-primary px-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-primary/90"
                data-testid="hospitality-catalog-load-more-button"
              >
                {content.hospitalityCatalog.loadMore}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
